import { Component, Inject, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { StoreService } from '../../../services/store/store.service';
import { Store } from '../../../types';
import { map, Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';

interface StoreDistrict {
  name: string;
  stores: Store[];
}

@Component({
  selector: 'app-store-picker-dialog',
  templateUrl: './store-picker-dialog.component.html',
  styleUrl: './store-picker-dialog.component.css',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatListModule,
    MatCheckboxModule,
    FormsModule,
    MatButtonModule,
  ],
})
export class StorePickerDialogComponent implements OnInit {
  storeDistricts$: Observable<StoreDistrict[]> = null!;

  selectedStores: Store[] = [];
  retainedUnavailableStores: Store[] = [];

  readonly dialogRef = inject(MatDialogRef<StorePickerDialogComponent>);
  constructor(
    private storeService: StoreService,
    @Inject(MAT_DIALOG_DATA) public data: { selectedStores: Store[] }
  ) {}
  ngOnInit(): void {
    this.selectedStores = [...this.data.selectedStores];
    this.retainedUnavailableStores = this.data.selectedStores.filter(store => store.isActive === false);
    this.storeDistricts$ = this.storeService.getAllStores().pipe(
      map((stores) => {
        const storeDistricts: StoreDistrict[] = [];
        stores.forEach((store) => {
          const district = storeDistricts.find(
            (sd) => sd.name === store.district
          );
          if (district) {
            district.stores.push(store);
          } else {
            storeDistricts.push({ name: store.district, stores: [store] });
          }
        });

        storeDistricts.sort((a, b) => a.name.localeCompare(b.name, 'da'));
        storeDistricts.forEach((sd) => {
          sd.stores.sort((a, b) => a.place.localeCompare(b.place, 'da'));
        });

        return storeDistricts;
      })
    );
  }

  isSelected(store: Store): boolean {
    return this.selectedStores.some(selected => selected.id === store.id);
  }

  isDistrictSelected(district: StoreDistrict) {
    return district.stores.every((store) =>
      this.isSelected(store)
    );
  }

  isDistrictIndeterminate(district: StoreDistrict) {
    const selectedCount = district.stores.filter((store) =>
      this.isSelected(store)
    ).length;
    return selectedCount > 0 && selectedCount < district.stores.length;
  }

  toggleDistrict(district: StoreDistrict, selectAll: boolean) {
    if (selectAll) {
      this.selectedStores = [...new Map([...this.selectedStores, ...district.stores].map(store => [store.id, store])).values()];
    } else {
      this.selectedStores = this.selectedStores.filter(
        (store) => !district.stores.some(candidate => candidate.id === store.id)
      );
    }
  }

  onStoreToggled(store: Store) {
    const isSelected = this.selectedStores.some((s) => s.id === store.id);
    if (isSelected) {
      this.selectedStores = this.selectedStores.filter(
        (s) => s.id !== store.id
      );
    } else {
      this.selectedStores = [...this.selectedStores, store];
    }
  }

  trackDistrict(index: number, district: StoreDistrict) {
    return district.name;
  }

  trackStore(index: number, store: Store) {
    return store.id; // or a unique identifier
  }
}
